import { useMsal } from '@azure/msal-react';
import { useCallback, useRef } from 'react';
import { acquireToken } from '../utils/msalTokenManager';
import { getRoleOverride } from '../utils/roleOverride';

/**
 * Message for an error response that carried no JSON body of its own.
 *
 * These are platform-level failures, not function failures, so they arrive with
 * an empty or HTML body and used to collapse into a bare "Request failed" — the
 * reason an over-size voice recording reported nothing a user could act on. The
 * two that actually happen are the 6 MB request-body 413 and the function-budget
 * 502/504.
 */
function fallbackErrorMessage(status) {
  if (status === 413) return 'That upload is too large. Try a shorter recording or a smaller file.';
  if (status === 504 || status === 502) return 'The server took too long to respond. Try again, or break the work into smaller pieces.';
  if (status === 429) return 'Too many requests right now. Wait a moment and try again.';
  if (status >= 500) return `Server error (${status}). Please try again.`;
  return `Request failed (${status}).`;
}

/**
 * Turn an error response into the Error we throw, carrying the response body
 * along with it.
 *
 * `err.message` is what almost every caller reads and is unchanged. The extra
 * fields exist for the few decisions that need to know WHICH failure this was
 * rather than what it said: an endpoint that returns a machine-readable `code`
 * (e.g. INSUFFICIENT_INVENTORY, which a purchase order can be re-sent through)
 * can be acted on without a caller matching on English prose.
 */
function apiError(status, payload) {
  const error = new Error(payload?.error || fallbackErrorMessage(status));
  error.status = status;
  error.code = payload?.code || null;
  error.body = payload || null;
  return error;
}

export function useApi() {
  const { instance } = useMsal();

  const getAccessToken = useCallback(async (forceRefresh = false) => {
    const account = instance.getActiveAccount();
    if (!account) {
      throw new Error('No account found');
    }

    // TECH DEBT: Using idToken as Bearer token is an Azure AD anti-pattern.
    // Should use access tokens with a custom API scope instead. Requires:
    //   1. Register a custom API scope in Azure AD app registration
    //   2. Update scopes here to request the custom scope
    //   3. Update backend audience validation in netlify/functions/utils/auth.js
    return acquireToken(instance, account, ['User.Read'], {
      forceRefresh,
      returnField: 'idToken',
      // The app-auth token is critical: if silent renewal fails (commonly the
      // hidden-iframe path timing out for guest/B2B accounts or when
      // third-party cookies are blocked — `timed_out` under MSAL v5, which
      // replaced v4's `monitor_window_timeout`), fall back to a full-page redirect,
      // which survives popup blockers and blocked cookies. Graph tokens keep
      // the default popup fallback since they're optional/incremental consent.
      fallback: 'redirect',
    });
  }, [instance]);

  // Ref ensures apiCall has a stable identity while always using the latest
  // getAccessToken (which updates when MSAL accounts change on token refresh).
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const apiCall = useCallback(
    async (endpoint, options = {}) => {
      const token = await getAccessTokenRef.current();

      const requestHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };

      // Include Graph API token as separate header when provided
      if (options.graphToken) {
        requestHeaders['X-Graph-Token'] = options.graphToken;
      }

      // Admin "view as" override — backend ignores this for non-admin callers.
      const override = getRoleOverride();
      if (override) {
        requestHeaders['X-Role-Override'] = JSON.stringify(override.roles);
      }

      const response = await fetch(`/api/${endpoint}`, {
        ...options,
        headers: requestHeaders,
      });

      // On 401, force-refresh the token and retry once
      if (response.status === 401) {
        const freshToken = await getAccessTokenRef.current(true);
        const retryResponse = await fetch(`/api/${endpoint}`, {
          ...options,
          headers: {
            ...requestHeaders,
            Authorization: `Bearer ${freshToken}`,
          },
        });

        if (!retryResponse.ok) {
          const payload = await retryResponse.json().catch(() => null);
          throw apiError(retryResponse.status, payload);
        }

        return retryResponse.json();
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw apiError(response.status, payload);
      }

      return response.json();
    },
    []
  );

  return { apiCall, getAccessToken };
}
